<template>
  <div>
    <div v-for="dDate in salesCheckDates" :key="dDate" class="margin-top-xs margin-bottom-m">
      <div v-for="sc in groupBySalesCheck[dDate].items" :key="sc.salesCheckNumber" class="border-bottom margin-top-xs margin-bottom-m">
        <div class="margin-bottom-xs">
          {{ ORDER_LOOKUP.salesCheckNumberText }}:
          <button class="sale-check-cta link-med" @click="handleSaleCheckClick(sc.salesCheckNumber, sc.salesPhoneNumber)">
            {{ sc.salesCheckNumber }}
          </button>
        </div>
      </div>

      <h2 class="h4 margin-bottom-xxs">
        {{ ORDER_LOOKUP.deliveryDateText }}
      </h2>
      <div class="font-weight-500">
        {{ dDate }}
      </div>
      <div class="border-bottom margin-top-xs margin-bottom-m"></div>
    </div>

    <div v-if="orderDetails?.ticketNumber" class="sales-check-number heavy">
      {{ ORDER_LOOKUP.salesCheckNumberText }}:
      <span id="ticket-number">{{ orderDetails.ticketNumber }}</span>
    </div>

    <div v-for="status in statusKeys" :key="status" class="margin-top-l">
      <h2 id="item-status" class="h4 margin-bottom-xxs">
        {{ status }}
      </h2>
      <div id="item-delivery-date" class="medium margin-bottom-xs">
        <span v-if="isProjectedArrivalTimeAvailable">ETA*: {{ orderDetails.deliveryStatus.projectedArrivalTime }}</span>
        {{ orderDetails.deliveryDate }}
      </div>

      <div v-for="statusItem in groupByStatus[status].items" :key="statusItem.modelName" class="border-bottom">
        <div id="item-description" class="font-weight-500 margin-bottom-xxs margin-top-xl description">
          {{ statusItem.description }}
        </div>
        <div id="item-quantity" class="gray-1-color margin-bottom-xxs">Qty: {{ statusItem.quantity }}</div>
        <div id="item-model" class="margin-bottom-m">{{ ORDER_LOOKUP.modelNumberText }}: {{ statusItem.modelName }}</div>
      </div>
    </div>

    <div class="order-details-info cell grid-x margin-top-l">
      <div v-if="address" class="cell medium-4 small-margin-bottom-l">
        <h2 id="delivery-address-section" class="h4 margin-bottom-xs">
          {{ ORDER_LOOKUP.deliveryAddressText }}
        </h2>
        <div v-if="address.addressLine1" id="delivery-address-line1">
          {{ address.addressLine1 }}
        </div>
        <div v-if="address.addressLine2" id="delivery-address-line2">
          {{ address.addressLine2 }}
        </div>
        <div v-if="address.city" id="delivery-address-city">
          {{ address.city }}
        </div>
        <div v-if="address.state" id="delivery-address-state">
          {{ address.state }}
        </div>
        <div v-if="address.postalCode" id="delivery-address-postalcode">
          {{ address.postalCode }}
        </div>
      </div>
      <div v-if="promisedTimeWindowStatus" id="delivery-window-section" class="cell medium-4 small-margin-bottom-l">
        <h2 class="h4 margin-bottom-xs">
          {{ ORDER_LOOKUP.deliveryWindowText }}
        </h2>
        <div>
          {{ deliveryWindowText }}
        </div>
      </div>
      <div v-if="orderDetails?.deliveryType" id="delivery-type-section" class="cell medium-4 small-margin-bottom-l">
        <h2 class="h4 margin-bottom-xs">
          {{ ORDER_LOOKUP.deliveryTypeText }}
        </h2>
        <div>
          {{ orderDetails.deliveryType }}
        </div>
      </div>
    </div>
    <p v-if="isProjectedArrivalTimeAvailable" class="small margin-bottom-m">
      {{ ORDER_LOOKUP.ETAText }}
    </p>
  </div>
</template>

<script setup>
import { computed, onUnmounted, onMounted } from 'vue';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useFurnitureStore } from '@/stores/ppe/useFurnitureStore';
import { fireTag } from '@vue-util/tagManager';
import { ORDER_LOOKUP, PPE_ERROR_TAG } from 'config';

const envPropsStore = useEnvPropsStore();
const furnitureStore = useFurnitureStore();
const isMew = computed(() => envPropsStore.envProps.isMew);
const getFurnitureStatus = (payload) => furnitureStore.GET_FURNITURE_STATUS(payload);
const clearFurnitureStatusState = async () => {
  await furnitureStore.CLEAR_FURNITURE_STATUS();
};

const furnitureStatus = computed(() => furnitureStore.furnitureStatus);
const orderDetails = computed(() => furnitureStatus.value?.orderDetails || {});
const groupByStatus = computed(() => {
  const orderItems = orderDetails.value?.items || [];
  const grouped = {};
  orderItems.forEach((od) => {
    if (grouped[od.status]) {
      grouped[od.status].items.push(od);
    } else {
      grouped[od.status] = { status: od.status, items: [od] };
    }
  });
  return grouped;
});
const statusKeys = computed(() => Object.keys(groupByStatus.value || {}));
const groupBySalesCheck = computed(() => {
  const sales = furnitureStatus.value?.multipleSales || [];
  const grouped = {};
  sales.forEach((saleCheck) => {
    if (grouped[saleCheck.deliveryDate]) {
      grouped[saleCheck.deliveryDate].items.push(saleCheck);
    } else {
      grouped[saleCheck.deliveryDate] = {
        deliveryDate: saleCheck.deliveryDate,
        items: [saleCheck],
      };
    }
  });
  return grouped;
});
const salesCheckDates = computed(() => Object.keys(groupBySalesCheck.value || {}));
const customerData = computed(() => orderDetails.value?.customerInformation);
const address = computed(() => customerData.value?.address || {});
const updateFurnitureApiError = computed(() => furnitureStore.updateFurnitureApiError);
const deliveryStatus = computed(() => orderDetails.value?.deliveryStatus || {});
const isProjectedArrivalTimeAvailable = computed(() => deliveryStatus.value?.projectedArrivalTime);
const deliveryWindowText = computed(() => {
  return `${orderDetails.value.deliveryDate}
    ${deliveryStatus.value?.beginPromisedTimeWindow} -
    ${deliveryStatus.value?.endPromisedTimeWindow}`;
});
const handleSaleCheckClick = async (scNumber, scPhone) => {
  const paddedSalesCheckNumber = String(scNumber).padStart(8, '0');
  const payloadObj = {
    salesCheckNumber: paddedSalesCheckNumber,
    salesPhoneNumber: scPhone,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    requestType: 'TICKET_PHONE_NUMBER',
    multipleOrders: furnitureStatus.value?.multipleOrders,
  };
  await getFurnitureStatus({
    payloadObj,
    envProp: {
      isMew: isMew.value,
    },
  });
  if (updateFurnitureApiError.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [updateFurnitureApiError.value?.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [updateFurnitureApiError.value?.message],
    });
  }
};

onMounted(() => window.scrollTo(0, 0));
onUnmounted(() => {
  if (!furnitureStatus.value?.multipleOrders) {
    clearFurnitureStatusState();
  }
});

const promisedTimeWindowStatus = () => {
  return deliveryStatus.value?.beginPromisedTimeWindow && deliveryStatus.value?.endPromisedTimeWindow;
};
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
</style>
